/**
 * Reconciliação Anúncios × ERP (C4): compara os anúncios do Mercado Livre com os produtos da
 * Plataforma pelo SKU (regra pura em `contracts/platform-extras.ts`).
 *
 * Declara o que não sabe: rota ausente = "aguardando contrato"; sem anúncio importado = lista
 * vazia dita como tal; anúncio vindo do simulador é rotulado (nunca apresentado como real).
 */
// Import explícito de `React` — mesmo motivo de `RmaDetalhe.tsx` (JSX clássico nos testes SSR).
import React, { type ReactElement } from "react";
import { StatusPanel } from "./StatusPanel.js";
import { AWAITING_CONTRACT_LABEL } from "../dashboard/kpis.js";
import {
  LISTING_RECONCILIATION_LABELS,
  reconcileListings,
  type ListingReconciliationStatus,
  type MarketplaceListing
} from "../contracts/platform-extras.js";
import { formatBrl, formatCount, formatDateTime } from "../format.js";
import { Badge, type BadgeTone } from "../ui/index.js";
import type { PlatformCatalogProduct } from "../api/types.js";

const TONE: Record<ListingReconciliationStatus, BadgeTone> = {
  matched: "success",
  price_diverged: "warning",
  stock_diverged: "warning",
  both_diverged: "danger",
  unlinked: "neutral"
};

const LISTING_STATUS_LABEL: Record<MarketplaceListing["status"], string> = { active: "Ativo", paused: "Pausado", closed: "Encerrado" };

export interface ListingReconciliationPanelProps {
  /** `null` = rota de anúncios não publicada neste ambiente. */
  listings: MarketplaceListing[] | null;
  /** `null` = catálogo da Plataforma indisponível (sem ele não há com o que comparar). */
  products: PlatformCatalogProduct[] | null;
}

export function ListingReconciliationPanel({ listings, products }: ListingReconciliationPanelProps): ReactElement {
  if (listings === null) {
    return (
      <StatusPanel
        kind="awaiting_source"
        title={`Catálogo de anúncios ${AWAITING_CONTRACT_LABEL}`}
        detail="A rota /api/platform/marketplace/listings não respondeu neste ambiente."
      />
    );
  }
  if (listings.length === 0) {
    return (
      <StatusPanel
        kind="empty"
        title="Nenhum anúncio do Mercado Livre importado ainda"
        detail="Os anúncios entram pela importação do canal. Sem eles não há o que reconciliar com o ERP."
      />
    );
  }
  if (products === null) {
    return (
      <StatusPanel
        kind="awaiting_source"
        title={`Catálogo da Plataforma ${AWAITING_CONTRACT_LABEL}`}
        detail="Há anúncios importados, mas sem o catálogo da Plataforma não há produto do ERP para comparar."
      />
    );
  }

  const { rows, productsWithoutListing } = reconcileListings(listings, products);
  const count = (status: ListingReconciliationStatus): number => rows.filter((row) => row.status === status).length;
  const diverged = count("price_diverged") + count("stock_diverged") + count("both_diverged");
  const simulated = listings.some((listing) => listing.source === "simulator");
  const lastSync = listings.map((listing) => listing.syncedAt).sort().at(-1);

  return (
    <div className="flex flex-col gap-4" data-reconciliation-rows={rows.length}>
      {simulated ? (
        <p className="m-0 rounded-panel border border-warning bg-warning-soft px-4 py-3 text-sm text-ink" data-listings-note="simulator">
          <strong className="font-bold">Como ler: </strong>
          estes anúncios são de demonstração (o Mercado Livre ainda não está conectado a esta Plataforma). Não use para decisão operacional.
        </p>
      ) : null}
      <p className="m-0 text-sm text-muted" data-reconciliation-summary={diverged}>
        {`${formatCount(rows.length)} anúncio(s): ${formatCount(count("matched"))} concordam, ${formatCount(diverged)} divergem, ${formatCount(count("unlinked"))} sem produto no ERP.`}
        {lastSync ? ` Última atualização: ${formatDateTime(lastSync)}.` : ""}
      </p>
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm" data-table="reconciliacao-anuncios">
          <caption className="sr-only">Anúncios do Mercado Livre comparados com os produtos do ERP</caption>
          <thead>
            <tr className="border-b border-border-strong bg-surface-soft text-xs uppercase tracking-wide text-muted">
              {["Anúncio", "SKU", "Preço (anúncio × Plataforma)", "Estoque (anúncio × Plataforma)", "Situação"].map((title) => (
                <th key={title} scope="col" className="whitespace-nowrap px-4 py-3 font-bold">{title}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map(({ listing, product, status }) => (
              <tr key={listing.id} data-record-id={listing.externalId} data-reconciliation-status={status} className="border-b border-border last:border-b-0">
                <td className="px-4 py-3">
                  <div className="font-bold text-ink">{listing.title}</div>
                  <div className="text-xs text-muted">{`${listing.externalId} · ${LISTING_STATUS_LABEL[listing.status]}`}</div>
                </td>
                <td className="px-4 py-3">{listing.sku ?? "—"}</td>
                <td className="px-4 py-3 tabular-nums">
                  {listing.priceCents === null ? "—" : formatBrl(listing.priceCents)}
                  {" × "}
                  {product?.sellableOffer ? formatBrl(product.sellableOffer.priceCents) : "—"}
                </td>
                <td className="px-4 py-3 tabular-nums">
                  {listing.stock === null ? "—" : formatCount(listing.stock)}
                  {" × "}
                  {product?.sellableOffer ? formatCount(product.sellableOffer.stock) : "—"}
                </td>
                <td className="px-4 py-3">
                  <Badge tone={TONE[status]}>{LISTING_RECONCILIATION_LABELS[status]}</Badge>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {productsWithoutListing.length > 0 ? (
        <section aria-label="Produtos sem anúncio ativo" data-products-without-listing={productsWithoutListing.length} className="flex flex-col gap-1">
          <h4 className="m-0 text-sm font-extrabold text-ink">Produtos do ERP sem anúncio ativo</h4>
          <p className="m-0 text-sm text-muted">{productsWithoutListing.map((product) => `${product.name} (${product.sku})`).join(" · ")}</p>
        </section>
      ) : null}
    </div>
  );
}
